// v3/design-system/primitives/button.tsx — Button primitive
// Shadcn/UI pattern: cva variants, forwardRef, cn() merging.
// Zero imports from v1/v2.

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../utils';

// Meridian buttons match the mock: compact bold controls on desktop
// (~34px, --shape-control radius), full 44px touch targets on the phone.
// Hover/pressed feedback is the M3 state layer (overlay of content color).
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[var(--shape-control)] text-[13px] font-bold ring-offset-background transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 md:text-[12.5px] [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'state-layer bg-primary text-primary-foreground shadow-[var(--sh-primary)] md:shadow-none',
        destructive: 'state-layer border border-[var(--ch-danger-fg)] bg-transparent text-[var(--ch-danger-fg)]',
        outline: 'state-layer border border-border bg-card text-foreground shadow-[var(--sh-sm)]',
        secondary: 'state-layer bg-secondary text-secondary-foreground',
        ghost: 'state-layer text-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-11 min-h-[44px] px-4 md:h-[34px] md:min-h-0 md:px-3.5',
        sm: 'h-9 min-h-[44px] px-3 md:h-7 md:min-h-0 md:px-2.5 md:text-[11.5px]',
        lg: 'h-12 min-h-[44px] px-8 md:h-10 md:min-h-0 md:px-6',
        icon: 'h-11 w-11 min-h-[44px] min-w-[44px] md:h-8 md:w-8 md:min-h-0 md:min-w-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
